import { useState } from "react";
import { GripVertical, Lock, X, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LEX_LABELS, TAB_NAMES, lockedModules } from "./vbUtils";

const GROUPS = [["core", "Core flow"], ["growth", "Customers & growth"], ["operations", "Operations"], ["later", "Coming later"]];

export function Section({ title, help, children, testId }) {
  return (
    <section className="border border-border p-4 space-y-3" data-testid={testId}>
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider">{title}</h3>
        {help && <p className="text-xs text-muted-foreground mt-0.5">{help}</p>}
      </div>
      {children}
    </section>
  );
}

export function FlowPicker({ r, flows, locked, set }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {Object.entries(flows).map(([k, f]) => {
        const off = locked || (k === "call") !== (r.product === "morrconnect");
        return (
          <button key={k} type="button" disabled={off && r.flow !== k} onClick={() => set({ flow: k })}
            className={`text-left border p-3 transition-colors ${r.flow === k ? "border-primary bg-primary/5" : "border-border hover:border-primary"} disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid={`vb-flow-${k}`}>
            <div className="text-sm font-semibold">{f.label}</div>
            <div className="text-xs text-muted-foreground">{f.help}</div>
          </button>
        );
      })}
    </div>
  );
}

export function ModuleChecklist({ r, modules, flows, set }) {
  const locked = lockedModules(r.flow, flows);
  const toggle = (k) => set({ modules: r.modules.includes(k) ? r.modules.filter((m) => m !== k) : [...r.modules, k] });
  return GROUPS.map(([g, title]) => (
    <div key={g}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">{title}</div>
      <div className="grid sm:grid-cols-2 gap-2">
        {modules.filter((m) => m.group === g).map((m) => {
          const lock = locked.includes(m.key);
          const on = lock || r.modules.includes(m.key);
          return (
            <label key={m.key} className={`flex gap-2 border p-2.5 text-sm ${!m.built ? "border-dashed opacity-50" : on ? "border-primary" : "border-border"} ${m.built && !lock ? "cursor-pointer" : ""}`} data-testid={`vb-module-${m.key}`}>
              <input type="checkbox" className="mt-0.5" checked={m.built && on} disabled={!m.built || lock} onChange={() => toggle(m.key)} data-testid={`vb-module-check-${m.key}`} />
              <span className="flex-1">
                <span className="font-medium flex items-center gap-1">{m.label}{lock && <Lock className="h-3 w-3" />}{!m.built && <span className="text-[10px] uppercase tracking-wider ml-1">Coming later</span>}</span>
                <span className="block text-xs text-muted-foreground">{m.desc}</span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  ));
}

export function TabOrder({ r, tabs, set }) {
  const [drag, setDrag] = useState(null);
  const drop = (target) => {
    if (drag === null || drag === target) return;
    const next = tabs.filter((t) => t !== drag);
    next.splice(next.indexOf(target), 0, drag);
    set({ tabs: next });
    setDrag(null);
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5" data-testid="vb-tab-order">
        {tabs.map((t) => (
          <span key={t} draggable onDragStart={() => setDrag(t)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(t)}
            className={`inline-flex items-center gap-1 border px-2 py-1 text-xs cursor-grab select-none ${drag === t ? "border-primary bg-primary/10" : "border-border bg-background"}`}
            data-testid={`vb-tab-${t}`}><GripVertical className="h-3 w-3 opacity-50" />{r.labels?.[t] || TAB_NAMES[t] || t}</span>
        ))}
      </div>
      <label className="text-xs flex items-center gap-2">Owners land on
        <select className="border border-input bg-background h-8 px-2 text-sm" value={r.default_tab || tabs[0]} onChange={(e) => set({ default_tab: e.target.value })} data-testid="vb-default-tab">
          {tabs.map((t) => <option key={t} value={t}>{r.labels?.[t] || TAB_NAMES[t] || t}</option>)}
        </select>
      </label>
    </div>
  );
}

export function Wording({ r, tabs, lexKeys, set }) {
  const field = "rounded-none h-8 text-sm";
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Tab labels</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {tabs.map((t) => (
            <Input key={t} className={field} placeholder={TAB_NAMES[t] || t} value={r.labels?.[t] || ""}
              onChange={(e) => set({ labels: { ...r.labels, [t]: e.target.value } })} data-testid={`vb-label-${t}`} />
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">What things are called</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {lexKeys.map((k) => (
            <label key={k} className="text-xs text-muted-foreground">{LEX_LABELS[k] || k}
              <Input className={`${field} mt-0.5`} value={r.lexicon?.[k] || ""} onChange={(e) => set({ lexicon: { ...r.lexicon, [k]: e.target.value } })} data-testid={`vb-lex-${k}`} />
            </label>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="text-xs text-muted-foreground">Storefront button
          <Input className={`${field} mt-0.5`} value={r.storefront?.cta || ""} onChange={(e) => set({ storefront: { ...r.storefront, cta: e.target.value } })} data-testid="vb-cta" />
        </label>
        <label className="text-xs text-muted-foreground">Catalog heading
          <Input className={`${field} mt-0.5`} value={r.storefront?.catalog_heading || ""} onChange={(e) => set({ storefront: { ...r.storefront, catalog_heading: e.target.value } })} data-testid="vb-catalog-heading" />
        </label>
      </div>
    </div>
  );
}

export function Categories({ r, set }) {
  const [v, setV] = useState("");
  const add = () => { const x = v.trim(); if (x && !r.categories.includes(x)) set({ categories: [...r.categories, x] }); setV(""); };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5" data-testid="vb-categories">
        {r.categories.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 border border-border px-2 py-0.5 text-xs">{c}
            <button type="button" onClick={() => set({ categories: r.categories.filter((x) => x !== c) })} data-testid={`vb-cat-remove-${c}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        {!r.categories.length && <span className="text-xs text-muted-foreground">None yet.</span>}
      </div>
      <div className="flex gap-2">
        <Input className="rounded-none h-8 text-sm" placeholder={r.product === "morrconnect" ? "Add a specialty" : "Add a category, e.g. Kitchen remodel"} value={v}
          onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} data-testid="vb-cat-input" />
        <Button type="button" variant="outline" size="sm" className="rounded-none h-8" onClick={add} data-testid="vb-cat-add">Add</Button>
      </div>
    </div>
  );
}

export function History({ items, onRestore, busy }) {
  if (!items?.length) return <p className="text-xs text-muted-foreground">No saves yet.</p>;
  return (
    <div className="divide-y border border-border" data-testid="vb-history">
      {items.map((h, i) => (
        <div key={h.id} className="flex flex-wrap items-start gap-2 p-2.5 text-xs" data-testid={`vb-history-${h.id}`}>
          <div className="flex-1 min-w-[200px]">
            <div className="font-medium">{new Date(h.at).toLocaleString()} · {h.by}</div>
            <div className="text-muted-foreground">{(h.summary || []).join(" · ")}</div>
          </div>
          {i > 0 && <Button size="sm" variant="ghost" className="rounded-none h-7 gap-1" disabled={busy} onClick={() => onRestore(h)} data-testid={`vb-restore-${h.id}`}><RotateCcw className="h-3 w-3" /> Restore</Button>}
          {i === 0 && <span className="text-[10px] uppercase tracking-wider text-primary">Current</span>}
        </div>
      ))}
    </div>
  );
}
